<script lang="ts">
import type { Component } from "vue";
import { makeLogger } from "@/shared/consola";
import {
    CoffeeIcon,
    CookieIcon,
    FaqIcon,
    LegalIcon,
    LoveIcon,
    PricingIcon,
    TickerIcon,
} from "@easy/ui/icons";
import SvgCoffeeLayout from "@/apps/coffee/project/onboarding/svg-previews/SvgCoffeeLayout.vue";
import SvgCookieLayoutModal from "@/apps/cookie/project/onboarding/svg-previews/SvgCookieLayoutModal.vue";
import FaqPreviewAside from "@/apps/faq/project/onboarding/svg-preview/PreviewAside.vue";
import SvgLegalLayout from "@/apps/legal/project/onboarding/svg-previews/SvgLegalLayout.vue";
import SvgLoveLayoutAvatars from "@/apps/love/project/onboarding/svg-previews/SvgLoveLayoutAvatars.vue";
import SvgPricingLayoutBasic from "@/apps/pricing/project/onboarding/svg-previews/SvgPricingLayoutBasic.vue";
import SvgTickerLayout from "@/apps/ticker/project/onboarding/svg-previews/SvgTickerLayout.vue";
import { InPageUsedTools } from "@/apps/page/page-project.interface";

const logger = makeLogger("ToolProjectSection");

const SVG_PICKER: Record<InPageUsedTools, { preview: Component; icon: Component }> = {
    coffee: { preview: SvgCoffeeLayout, icon: CoffeeIcon },
    cookie: { preview: SvgCookieLayoutModal, icon: CookieIcon },
    faq: { preview: FaqPreviewAside, icon: FaqIcon },
    legal: { preview: SvgLegalLayout, icon: LegalIcon },
    love: { preview: SvgLoveLayoutAvatars, icon: LoveIcon },
    pricing: { preview: SvgPricingLayoutBasic, icon: PricingIcon },
    ticker: { preview: SvgTickerLayout, icon: TickerIcon },
};
</script>
<script setup lang="ts">
import { PageSection } from "@/apps/page/page.interfaces";
import { computed, ref } from "vue";
import { useAsyncState } from "@vueuse/core";
import { projectsService } from "@/shared/projects/projects.service";
import { TOOL_APP_TYPES } from "@/shared/tool-app.types";
import FooterBody from "@/views/tools/legal/footers/FooterBody.vue";
import LoveWallBody from "@/views/tools/love/wall/LoveWallBody.vue";
import FaqBody from "@/views/tools/faq/FaqBody.vue";
import CoffeeBody from "@/views/tools/coffee/CoffeeBody.vue";
import CookieBody from "@/views/tools/cookie/CookieBody.vue";
import TickerBody from "@/views/tools/ticker/TickerBody.vue";

import PricingBody from "@/views/tools/pricing/PricingBody.vue";
import {
    prepareCoffee,
    prepareCookie,
    prepareFaq,
    prepareFooter,
    preparePricing,
    prepareTicker,
    prepareWall,
} from "@/shared/projects/prepareData";
import {
    mapThemeToCoffeeColors,
    mapThemeToCookieColors,
    mapThemeToFaqColors,
    mapThemeToLegalColors,
    mapThemeToLoveColors,
    mapThemeToPricingColors,
    mapThemeToTickerColors,
    ThemeToToolProjectStylesMappers,
} from "@/shared/projects/prepare-theme";
import { PageProject } from "@/apps/page/page-project.interface";
import { useRenderPreviewStore } from "@/views/store/render-preview.store";
import { storeToRefs } from "pinia";
import BindSectionWithToolPanel from "@/views/page/BindSectionWithToolPanel.vue";
import { ButtonSecondaryMd } from "@easy/ui/buttons";
import { RocketIcon } from "@easy/ui/icons";
import { useEditorActions } from "@/views/tools/page/utils/useEditorActions";

const { publishToolProject } = useEditorActions();
const renderPreviewStore = useRenderPreviewStore();
const { isPreview } = storeToRefs(renderPreviewStore);
const props = withDefaults(
    defineProps<{
        type: InPageUsedTools;
        fontFamily: PageProject["fontFamily"];
        uuid?: PageProject["uuid"];
        webId?: PageSection["webId"];
        sectionId?: PageSection["id"];
        styleValues?: PageSection["styleValues"];
        projectName?: string;
        isListOfItemsToPick?: boolean;
        isProjectPublished?: boolean;
    }>(),
    {
        isListOfItemsToPick: false,
    }
);

const adaptStyleColors = (mapperLogic: ThemeToToolProjectStylesMappers, fallbackData: unknown) => {
    const toolProps = mapperLogic({
        id: "",
        // @ts-expect-error for now TODO: fix this
        colors: props.styleValues,
        name: "",
        font: props.fontFamily,
    });
    return Object.keys(toolProps)
        .filter((k) => k !== "theme")
        .reduce((acc, key) => {
            // @ts-expect-error for now TODO: fix this
            acc[key] = toolProps[key] || fallbackData?.[key] || "";
            return acc;
        }, {});
};

const projectData = ref({});
const isWithoutTheProjectConnected = computed(() => !props.uuid);
const projectSetup = computed(() => {
    const data: any = projectData.value;
    let holder: any = {};
    switch (props.type as InPageUsedTools) {
        case "coffee":
            return {
                ...(data ? prepareCoffee(data) : {}),
                ...adaptStyleColors(mapThemeToCoffeeColors, data),
            };
        case "cookie":
            return {
                ...(data ? prepareCookie(data) : {}),
                ...adaptStyleColors(mapThemeToCookieColors, data),
            };
        case "faq":
            return {
                ...(data ? prepareFaq(data) : {}),
                ...adaptStyleColors(mapThemeToFaqColors, data),
            };
        case "legal":
            return {
                ...(data ? prepareFooter(data) : {}),
                ...adaptStyleColors(mapThemeToLegalColors, data),
            };
        case "love":
            return {
                ...(data ? prepareWall(data) : {}),
                ...adaptStyleColors(mapThemeToLoveColors, data),
            };
        case "pricing":
            return {
                ...(data ? preparePricing(data) : {}),
                ...adaptStyleColors(mapThemeToPricingColors, data),
            };
        case "ticker":
            holder = adaptStyleColors(mapThemeToTickerColors, data.colors);
            return {
                ...(data ? prepareTicker(data) : {}),
                fontFamily: holder.font,
                colors: { ...holder.colors },
            };
        /*case "timer":
            return {
                ...(data ? prepareTimer(data) : {}),
                ...adaptStyleColors(mapThemeToTimerColors, data),
            };*/
        default:
            return {};
    }
});
const projectInsert = computed(() => ({
    ...projectData.value,
    ...projectSetup.value,
}));

useAsyncState(
    async () => {
        // const { type, projectData = { uuid: "" } } = props.section;
        logger.trace("loading tool project for", props);
        if (!props.uuid) {
            return {};
        }
        const response = await projectsService.getOneForRender({
            tool: props.type,
            uuid: props.uuid,
        });
        logger.trace("response", response);
        if (props.type === TOOL_APP_TYPES.pricing) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            const { pricing, ecPricing, ecVariants } = response;
            return {
                ...pricing,
                ecPricing,
                ecVariants,
            };
        }
        return response;
    },
    {},
    {
        onSuccess(data) {
            projectData.value = data || {};
        },
    }
);
</script>

<template>
    <div :id="webId">
        <template v-if="isWithoutTheProjectConnected || !isProjectPublished">
            <div
                v-if="isPreview"
                class="flex justify-evenly p-6 gap-3 lg:gap-12 lg:p-20 items-center z-[100]"
            >
                <section class="flex justify-center flex-col items-center">
                    <div
                        class="w-14 h-14 flex justify-center items-center rounded bg-opacity-10"
                        :class="`bg-${type}-500`"
                    >
                        <component
                            class="w-7 h-7"
                            :class="`text-${type}-500`"
                            :is="SVG_PICKER[type].icon"
                        />
                    </div>

                    <template v-if="isWithoutTheProjectConnected">
                        <h3 class="mt-6 mb-8 font-medium text-xl md:text-2xl">
                            {{
                                $t("page.bind_section_panel.title", {
                                    tool: type,
                                })
                            }}
                        </h3>
                        <BindSectionWithToolPanel :tool="type" :sectionId="sectionId" />
                    </template>
                    <template v-else-if="!isProjectPublished">
                        <h3 class="mt-6 mb-8 font-medium text-xl md:text-2xl">
                            {{
                                $t("page.bind_section_panel.title_publish", {
                                    tool: type,
                                })
                            }}
                        </h3>
                        <div class="p-2 sm:w-full md:w-1/2 text-center">
                            {{
                                $t("page.bind_section_panel.description_publish", {
                                    name: projectName,
                                })
                            }}
                        </div>
                        <ButtonSecondaryMd
                            class="m-2 sm:w-full md:w-1/2"
                            @click="publishToolProject({ uuid, tool: type })"
                        >
                            <RocketIcon />&nbsp;
                            <span>
                                {{ $t("page.bind_section_panel.edit_and_publish") }}
                            </span>
                        </ButtonSecondaryMd>
                    </template>
                </section>
                <aside
                    class="rounded p-4 hidden md:flex justify-center items-center"
                    :style="{ backgroundColor: 'var(--et-colors-background)' }"
                >
                    <component class="w-3/4 lg:w-auto" :is="SVG_PICKER[type].preview" />
                </aside>
            </div>
        </template>
        <template v-else>
            <LoveWallBody
                v-if="type === TOOL_APP_TYPES.love"
                :wall="projectInsert"
                :hideLogo="true"
            />
            <FaqBody v-if="type === TOOL_APP_TYPES.faq" :faq="projectInsert" :hideLogo="true" />
            <PricingBody
                v-if="type === TOOL_APP_TYPES.pricing"
                :pricing="projectInsert"
                :hideLogo="true"
            />
            <FooterBody v-if="type === TOOL_APP_TYPES.legal" :footer="projectInsert" />
            <template v-if="isPreview || isListOfItemsToPick">
                <CoffeeBody
                    v-if="type === TOOL_APP_TYPES.coffee"
                    :coffee="projectInsert"
                    :nonFixed="isListOfItemsToPick"
                />
                <CookieBody
                    v-if="type === TOOL_APP_TYPES.cookie"
                    :cookie="projectInsert"
                    :nonFixed="isListOfItemsToPick"
                />
                <div :class="[isListOfItemsToPick ? '' : 'absolute bottom-0 z-10']">
                    <TickerBody v-if="type === TOOL_APP_TYPES.ticker" :ticker="projectInsert" />
                </div>
            </template>
        </template>
    </div>
</template>
